import React, {useCallback, useEffect, useMemo, useState} from "react";
import {createUUId, stringComparator} from "../../../../core/services/utils/utils";
import PrintOrderTemplate from "./template";
import PrintOrderViewPage from "./page";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import Sizes from "../../../../core/models/static/sizes";
import ElementIds from "../../../../core/models/element-ids";
import useQuery from "../../../hooks/use-query";
import {useParams} from "react-router-dom";
import {usePartnerInformation, usePartnerInformationController} from "../../../hooks/use-partner-information";

export const PrintOrderViewTableTypes = {
    products: 'products',
    notes: 'notes',
}

/**
 * Fetches the elements that are needed for the computation of the print pages.
 */
export const getElements = () => ({
    page: document.getElementById(ElementIds.printInvoiceView.page),
    table: document.getElementById(ElementIds.printInvoiceView.table),
    pageHeader: document.getElementById(ElementIds.printInvoiceView.pageHeader),
    tablesDistance: document.getElementById(ElementIds.printInvoiceView.tablesDistance),
    productsTable: {
        header: document.getElementById(ElementIds.printInvoiceView.productsTable.header),
        body: document.getElementById(ElementIds.printInvoiceView.productsTable.body),
        bodyFiller: document.getElementById(ElementIds.printInvoiceView.productsTable.bodyFiller),
        footerContinue: document.getElementById(ElementIds.printInvoiceView.productsTable.footerContinue),
        footer: document.getElementById(ElementIds.printInvoiceView.productsTable.footer),
    },
    notesTable: {
        header: document.getElementById(ElementIds.printInvoiceView.notesTable.header),
        body: document.getElementById(ElementIds.printInvoiceView.notesTable.body),
        footerContinue: document.getElementById(ElementIds.printInvoiceView.notesTable.footerContinue),
    }

})

/**
 * Fetches the static heights of the template elements. These aid in the process of determining the print pages.
 * @param {any} elements
 */
export const getHeights = (elements) => ({
    productsTableStaticHeight: elements.productsTable.header.scrollHeight + elements.productsTable.footer.scrollHeight,
    productsTableStaticHeightWithContinue: elements.productsTable.header.scrollHeight + elements.productsTable.footerContinue.scrollHeight,
    productsTableFooterStaticHeight: elements.productsTable.footer.scrollHeight,
    productsTableFooterStaticHeightWithContinue: elements.productsTable.footerContinue.scrollHeight,
    notesTableStaticHeight: elements.notesTable.header.scrollHeight,
    notesTableStaticHeightWithContinue: elements.notesTable.header.scrollHeight + elements.notesTable.footerContinue.scrollHeight,
    tablesDistance: elements.tablesDistance.scrollHeight,
})


/**
 * @typedef {{
 *     pageNumber: int,
 *     totalPages: int,
 *     entities: {name: string, range: [number, number], useContinueFooter: boolean, spacerHeight: number}[],
 *     key: string,
 * }} PrintOrderPage
 */

const PrintOrderView = ({salesOrder = false}) => {
    const [query] = useQuery();
    const params = useParams();
    const [printInfoReady, setPrintInfoReadyReady] = useState(false);
    const [order, setOrder] = useState(null);
    const [rules, setRules] = useState("");
    const [pages, setPages] = useState([]);
    const isMounted = useIsMounted();

    const partnerInformation = usePartnerInformation();
    const {getPartnerInformation} = usePartnerInformationController();
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation])
    const partnerInfoReady = !!partner;

    const showAddresses = useMemo(() => query?.showAddresses === 'true', [query?.showAddresses])
    const showPaymentTerms = useMemo(() => query?.showPaymentTerms === 'true', [query?.showPaymentTerms])
    const showPrices = useMemo(() => query?.showPrices === 'true', [query?.showPrices])
    const showTransactions = useMemo(() => query?.showTransactions === 'true', [query?.showTransactions])

    const ready = useMemo(() => printInfoReady && partnerInfoReady, [printInfoReady, partnerInfoReady]);

    /**
     * Fetches the full information of the order from the server and if the result of the api is successful then
     * sets the data.
     */
    const getOrderInformation = useCallback(async () => {
        setPrintInfoReadyReady(false);
        const response = await (
            salesOrder
                ? Api.getSalesOrderInformationForCustomer(params.customerId, params.orderId)
                : Api.getQuoteInformationForCustomer(params.customerId, params.orderId)
        );
        if (!isMounted()) return;
        if (response?.resultFlag) {
            // sort the appliedTransactions by their type of the id so that the gift card can be at the beginning
            // or at the end.
            response.data.appliedTransactions = response.data?.appliedTransactions?.sort((a, b) => stringComparator(a.transactionTypeName, b.transactionTypeName));
            setOrder(response.data);
        }
    }, [params.customerId, params.orderId, isMounted, salesOrder])

    /**
     * Loads the selected order or credit as soon as the component loads
     */
    useEffect(() => {
        void getOrderInformation();
    }, [getOrderInformation])

    /**
     * Fetches the full information of the current partner that the invoice belongs to from the server and if the result
     * of the api is successful then sets the data.
     * @type {(function(): Promise<void>)}
     */
    const getPartnerInformationAndSetState = useCallback(async () => {
        const response = await getPartnerInformation();
        if (!isMounted()) return;
        if (!!response) {
            setRules(response?.saleRegulationDescription ?? "");
        }
    }, [getPartnerInformation, isMounted])

    /**
     * Loads the selected invoice as soon as the component loads
     * As soon as the component mounts, the print dialog is also opened
     */
    useEffect(() => {
        void getPartnerInformationAndSetState();
    }, [getPartnerInformationAndSetState])

    /**
     * Creates the pages that are to be rendered for printing.
     * @type {(function(elements: any, heights: any): PrintOrderPage[])}
     */
    const createPages = useCallback((elements, heights) => {
        const MAX_PAGE_HEIGHT = elements.page.clientHeight - (Sizes.defaultPagePadding * 2) - elements.pageHeader.scrollHeight;
        /**@type {HTMLCollection}*/
        const products = elements.productsTable.body.children;
        /**@type {HTMLCollection}*/
        const notes = elements.notesTable.body.children;

        // Indices to keep track of which product and note are being processed
        let productsIndex = 0;
        let notesIndex = 0;

        // Array to store the pages
        let pages = [];
        let productsNormalFooterAdded = false;

        while (productsIndex < products.length || notesIndex < notes.length) {
            let currentPageProducts = [];
            let currentPageNotes = [];
            let remainingHeight = MAX_PAGE_HEIGHT;
            let useProductsContinueFooter = false;

            // Add products
            while (productsIndex < products.length) {
                let nextProductHeight = products[productsIndex].scrollHeight;

                // Check if we can add more products without the "continue" footer
                if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeight) {
                    useProductsContinueFooter = true;
                    break;
                }

                currentPageProducts.push(productsIndex);
                remainingHeight -= nextProductHeight;
                productsIndex++;
            }

            // Try to add more products with the "continue" footer
            if (useProductsContinueFooter) {
                while (productsIndex < products.length) {
                    let nextProductHeight = products[productsIndex].scrollHeight;

                    if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeightWithContinue) {
                        break;
                    }

                    currentPageProducts.push(productsIndex);
                    remainingHeight -= nextProductHeight;
                    productsIndex++;
                }
            }

            if (!productsNormalFooterAdded && !useProductsContinueFooter) {
                productsNormalFooterAdded = true;
            }

            // Reduce the remaining height by the distance between the products and notes tables or the products table footer
            if (currentPageProducts.length > 0) {
                remainingHeight -= heights.tablesDistance;
            }
            if (useProductsContinueFooter) {
                remainingHeight -= heights.productsTableFooterStaticHeightWithContinue;
            }
            if (!useProductsContinueFooter && productsNormalFooterAdded) {
                remainingHeight -= heights.productsTableFooterStaticHeight;
            }

            // Add notes
            if (notes.length > 0) {
                if (remainingHeight > 0) {
                    // Add notes
                    while (notesIndex < notes.length) {
                        let nextNoteHeight = notes[notesIndex].scrollHeight;

                        if ((remainingHeight - nextNoteHeight) < heights.notesTableStaticHeight) {
                            break;
                        }

                        currentPageNotes.push(notesIndex);
                        remainingHeight -= nextNoteHeight;
                        notesIndex++;
                    }


                    // Try to add more notes with the "continue" footer
                    while (notesIndex < notes.length) {
                        let nextNoteHeight = notes[notesIndex].scrollHeight;

                        if ((remainingHeight - nextNoteHeight) < heights.notesTableStaticHeightWithContinue) {
                            break;
                        }

                        currentPageNotes.push(notesIndex);
                        remainingHeight -= nextNoteHeight;
                        notesIndex++;
                    }
                }
            }

            // Add the current page to the pages array
            pages.push({
                products: currentPageProducts,
                notes: currentPageNotes,
                remainingHeight: remainingHeight,
                useProductsContinueFooter: useProductsContinueFooter,
            });
        }

        // if the footer was not added, add it now
        if (!productsNormalFooterAdded) {
            pages.push({
                products: [],
                notes: [],
                remainingHeight: MAX_PAGE_HEIGHT - heights.productsTableFooterStaticHeight,
                useProductsContinueFooter: false,
            });
        }

        // add the product filled to any page, so it can fill up the space.
        for (const page of pages) {
            if (page.products.length === 0) {
                page.products.push(products.length - 1)
            }
        }


        return pages.map((page, index, array) => {
            const productsEntity = {
                name: PrintOrderViewTableTypes.products,
                range: [
                    page.products.reduce((min, p) => p < min ? p : min, Number.MAX_SAFE_INTEGER),
                    page.products.reduce((max, p) => p > max ? p : max, Number.MIN_SAFE_INTEGER),
                ].filter(e => e !== Number.MIN_SAFE_INTEGER && e !== Number.MAX_SAFE_INTEGER),
                useContinueFooter: page.useProductsContinueFooter,
                spacerHeight: page.remainingHeight,
            }
            const notesEntity = {
                name: PrintOrderViewTableTypes.notes,
                range: [
                    page.notes.reduce((min, p) => p < min ? p : min, Number.MAX_SAFE_INTEGER),
                    page.notes.reduce((max, p) => p > max ? p : max, Number.MIN_SAFE_INTEGER),
                ].filter(e => e !== Number.MIN_SAFE_INTEGER && e !== Number.MAX_SAFE_INTEGER),
                useContinueFooter: index !== array.length - 1,
                spacerHeight: 0,
            }

            const entities = [];
            if (productsEntity.range.length === 2 || !productsEntity.useContinueFooter) {
                entities.push(productsEntity);
            }
            if (notesEntity.range.length === 2) {
                entities.push(notesEntity);
            }
            return ({
                entities: entities,
                key: createUUId(),
                pageNumber: index + 1,
                totalPages: array.length,
            })
        });
    }, [])

    /**
     * Sets the pages that are to be rendered for printing.
     * @type {(function(): void)}
     */
    const setPrintPages = useCallback(() => {
        const elements = getElements();
        const heights = getHeights(elements);
        setPages(createPages(elements, heights));
    }, [createPages])

    /**
     * With each change in Invoice:
     * - sets the ready state based on the existence of the invoice.
     * - if the ready state is set to true, waits for 2 seconds and then calculates the necessary info from the DOM.
     */
    useEffect(() => {
        if (!order || !partner) {
            return setPrintInfoReadyReady(false);
        }
        // give browser to render its elements and load the logo.
        setTimeout(() => {
            setPrintPages();
            setPrintInfoReadyReady(true);
        }, 2000)
    }, [order, partner, setPrintPages])


    return (
        <>
            <div className={'print-invoice-view'} data-mui-color-scheme="light">
                {
                    !ready &&
                    <div className={'min-vh-100 d-flex justify-content-center align-items-center'}>
                        <div className={'loading print-invoice-page'}>
                            <div/>
                        </div>
                    </div>
                }
                {
                    ready && !!order &&
                    <div className={'pages'}>
                        {
                            pages.map(page => (
                                <PrintOrderViewPage
                                    key={page.key}
                                    page={page}
                                />
                            ))
                        }
                    </div>
                }
                {
                    !!order && !!partner &&
                    <PrintOrderTemplate
                        salesOrder={salesOrder}
                        order={order}
                        partner={partner}
                        showTransactions={showTransactions}
                        showAddresses={showAddresses}
                        showPaymentTerms={showPaymentTerms}
                        showPrices={showPrices}
                        rules={rules}
                    />
                }
            </div>
        </>
    )
}

export default PrintOrderView;
